<template>
    <Transition>
        <Teleport to="#modals">
            <Modal
                v-show="active && isDesktop"
                :active="active && isDesktop"
                type="full"
                class="select-none [-webkit-touch-callout:none]"
                @close="emits('close')"
            >
                <!-- The whole lightbox is select-none, not just the counter:
                     iPadOS turns a quick double tap on an arrow into a
                     selection of the nearest selectable node, so making the
                     counter alone unselectable moved the highlight onto the
                     whole modal. Scoped here, not in the shared Modal.vue,
                     whose other uses hold text guests do select. -->
                <p
                    v-if="total"
                    class="fixed top-3 left-3 md:top-5 md:left-5 inline-flex items-center gap-2.5 py-2 px-2.5 rounded-sm bg-white text-brand-2-950 font-semibold text-xssss tabular-nums"
                >
                    <span
                        class="size-[18px]"
                        v-html="CameraIcon"
                    ></span>
                    {{ currentSlide }} / {{ total }}
                </p>

                <slot />
            </Modal>
        </Teleport>
    </Transition>
</template>

<script setup>
    import { ref, onMounted, watch } from 'vue';

    import Modal from '@js/src/components/Modal.vue';

    import { useBackToClose } from '@js/src/composables/useBackToClose';

    import CameraIcon from '@img/icons/camera.svg?raw';

    const props = defineProps([
        'active',
        'total',
        'slide',
    ]);

    const emits = defineEmits(['close']);

    const sliderName = 'property-gallery';
    const currentSlide = ref(1);

    // Below 501px the vertical-scroll MobileGalleryOverlay takes over; this
    // carousel only activates on desktop. Gating reloadSlider() (not just the
    // v-show) is what stops Flickity ever loading the carousel cells on mobile.
    // The variant is fixed at open time to stay in lockstep with the overlay.
    const isDesktop = ref(true);

    // Back button dismisses the lightbox instead of navigating off the house page.
    const backToClose = useBackToClose(() => emits('close'));

    watch(() => props.active, (active) => {
        if (!active) {
            // Consumes the pushed history entry on UI-driven (X / backdrop) closes;
            // no-op when a back press already closed us or we never opened (mobile).
            backToClose.close();
            document.removeEventListener('selectionchange', clearSelection);

            return;
        }

        isDesktop.value = !window.matchMedia('(max-width: 500px)').matches;

        if (isDesktop.value) {
            backToClose.open();
            reloadSlider();
            document.addEventListener('selectionchange', clearSelection);
        }
    });

    onMounted(() => {
        window.addEventListener('landedHouses/flickity/change', (e) => {
            if (e.detail.name === sliderName) {
                currentSlide.value = e.detail.index + 1;
            }
        });
    });

    // Belt and braces for the select-none above: WebKit can still snap a
    // double tap's selection onto page text behind the lightbox. Nothing is
    // meant to be selected while it is open, so drop any range it makes.
    function clearSelection() {
        const selection = window.getSelection();

        if (selection && !selection.isCollapsed) {
            selection.removeAllRanges();
        }
    }

    function reloadSlider() {
        const slider = LandedHouses.sliders[sliderName];

        if (slider) {
            slider.select(props.slide, true, true);

            setTimeout(() => {
                slider.reloadCells();
                slider.resize();
            }, 300);
        }
    }
</script>
